import JsonResponse from "csi/comm/JsonResponse";
import { C8Node, cnl, ICache } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { runInAction } from "mobx";

type ParameterMap = Record<string, any>;
type QueryBuilder = (contextUrl?: cnl, params?: ParameterMap) => Search;

const QUERIES = new Map<string, QueryBuilder>();

QUERIES.set("BaseProduct.ProductPresentation", (contextUrl?: cnl, params?: ParameterMap) => {
	const type = params?.type;
	const search = new Search();

	if (type) {
		search.byType(type);
	}

	search.setLimitPath(contextUrl, pathCreate("PPProducts"));
	return search;
});

QUERIES.set("BaseProduct.ProductPresentationSet", (contextUrl?: cnl, params?: ParameterMap) => {
	const type = params?.type;
	const search = new Search();

	if (type) {
		search.byType(type);
	}

	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProduct"));
	return search;
});

QUERIES.set("DSLine.SizeChartRevision.DSLinesOnSizeChartRevision", (contextUrl: cnl, params?: ParameterMap) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:Items");
	const q = search.getQuery();
	q.Or()
		.Node("Type", xml.OP_EQUALS, "SizeChartDimension")
		.Node("Type", xml.OP_EQUALS, "DSLineDivider");

	if (params?.isLinkedToShape) {
		q.Attr("ShapeSysID", xml.OP_NOT_EQUALS, "");
	}
	if (params?.wasLinkedToShape) {
		q.Attr("FormerShapeSysID", xml.OP_NOT_EQUALS, "");
	}
	return search;
});

QUERIES.set("DSLine.SizeChartRevision.LinkedShapeSizeChartDSLines", (contextUrl: cnl) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:__Parent__/Child:ShapeSizeChart/Child:CurrentRevision/Child:Items");
	return search;
});

QUERIES.set("DSLine.SizeChartRevision.NotLinkedStyleSizeChartDSLines", (contextUrl: cnl, params?: ParameterMap) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:Items");
	const q = search.getQuery();
	const qOr = q.Or(); 
	qOr.And()
		.Node("Type", xml.OP_EQUALS, "SizeChartDimension")
		.Attr("Special", xml.OP_EQUALS, false, xml.path("Actual"));
	qOr.Node("Type", xml.OP_EQUALS, "DSLineDivider");
	q.Attr("ShapeSysID", xml.OP_EQUALS, "");
		
	return search;
});

QUERIES.set("DSLineItem.ApparelBOMRevision.StyleBOMDSLineItem", (contextUrl: cnl, params?: ParameterMap) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:Items");
	search.addDependencyPaths(["Child:DSSection"]);
	const q = search.getQuery();

	if (params?.isLinkedToTheme) {
		q.Attr("ThemeBOMSysID", xml.OP_NOT_EQUALS, "");
	}
	if (params?.wasLinkedToTheme) {
		q.Attr("FormerThemeBOMSysID", xml.OP_NOT_EQUALS, "");
	}
	return search;
});

QUERIES.set("DSLineItem.ApparelBOMRevision.SourceBOMDSLineItem", (contextUrl: cnl, params?: ParameterMap) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:Items");
	search.addDependencyPaths(["Child:DSSection"]);
	const q = search.getQuery();
	const qOr = q.Or();

	if (params?.linkedDSLineItemSysIDsList) {
		params?.linkedDSLineItemSysIDsList.forEach((id) => {
			qOr.Attr("LinkedDSLineItemSysID", xml.OP_EQUALS, id);
		});
	}
	return search;
});

QUERIES.set("DSLineItem.ApparelBOMRevision.LinkedThemeBOMDSLineItem", (contextUrl: cnl) => {
	const search = new Search();
	search.setLimitPath(contextUrl, "Child:__Parent__/Child:StyleBOMLinkedThemeBOM/Child:CurrentRevision/Child:Items");
	search.addDependencyPaths(["Child:DSSection"]);
	return search;
});

QUERIES.set("Style.Theme", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("Style");

	const searchQuery = search.getQuery()
		.Node(xml.PROP_URL, xml.OP_EQUALS, contextUrl, "Child:Theme");

	if (params?.presentationUrl) {
		searchQuery.Node(xml.PROP_URL, xml.OP_EQUALS, params.presentationUrl, "Parent:PPProducts");
	}

	return search;
});

QUERIES.set("Theme.Style", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Theme");
	search.setLimitPath(contextUrl, "Child:Theme");
	return search;
});

QUERIES.set("ThemeMaster.Style", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ThemeMaster");
	search.setLimitPath(contextUrl, "Child:Theme/Child:__Parent__");
	return search;
});

QUERIES.set("ThemeMaster.Theme", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ThemeMaster");
	search.setLimitPath(contextUrl, pathCreate("__Parent__"));
	return search;
});

QUERIES.set("Theme.ThemeMaster", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Theme");
	search.setLimitPath(contextUrl, pathCreate("Themes"));
	return search;
});

QUERIES.set("ProductPresentationSet.ThemeMaster", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("ProductPresentationSet");
	search.getQuery()
		.RefAttr("__Parent__", xml.OP_EQUALS, contextUrl, pathCreate("Items", "PPIProduct", "Theme"));

	if (params?.presentationUrl) {
		search.getQuery()
			.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, params.presentationUrl);
	}

	return search;
});

QUERIES.set("Style.ThemeMaster", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("Style");
	search.setLimitPath(contextUrl, "Child:Themes/Parent:Theme");
	return search;
});

QUERIES.set("ColorwayOrColorMaterial.ProductPresentation", (contextUrl?: cnl, params?: ParameterMap) => {
	const type = params?.type;
	const search = new Search();

	if (type) {
		search.byType(type);
	}

	search.setLimitPath(contextUrl, pathCreate("PPProducts", "ProductColors"));
	return search;
});

QUERIES.set("Comment.DataSheetPage", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Comment");
	search.setLimitPath(contextUrl, pathCreate("Comments"));
	return search;
});

QUERIES.set("Style.Material.MainMaterials", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Style");
	search.setLimitPath(contextUrl, "Parent:BOMMainMaterials/Child:__Parent__");
	return search;
});

QUERIES.set("ApparelBOM.Style", (contextUrl?: cnl) => {
	return getSearch("ApparelBOM.DataSheets", contextUrl);
});

QUERIES.set("ApparelBOM.ProductSource", (contextUrl?: cnl) => {
	return getSearch("ApparelBOM.DataSheets", contextUrl);
});

QUERIES.set("ApparelBOM.DataSheets", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ApparelBOM");
	search.setLimitPath(contextUrl, "Child:DataSheets");
	return search;
});

//This is a special case, because the Theme and Style both have BOM of type ApparelBOM.
QUERIES.set("ApparelBOM.Theme", QUERIES.get("ApparelBOM.Style"));

QUERIES.set("MaterialBOM.Material", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("MaterialBOM");
	search.setLimitPath(contextUrl, "Child:DataSheets");
	return search;
});

QUERIES.set("FoodBOM.Food", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("FoodBOM");
	search.setLimitPath(contextUrl, "Child:DataSheets");
	return search;
});

QUERIES.set("ProductPresentationSet.BaseProduct", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("ProductPresentationSet");
	search.getQuery()
		.RefAttr("PPIProduct", xml.OP_EQUALS, contextUrl, pathCreate("Items"));

	if (params?.presentationUrl) {
		search.getQuery()
			.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, params.presentationUrl);
	}

	return search;
});

QUERIES.set("ProductPresentationSet.Theme", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("ProductPresentationSet");
	search.getQuery()
		.RefAttr("Theme", xml.OP_EQUALS, contextUrl, pathCreate("Items", "PPIProduct"));

	if (params?.presentationUrl) {
		search.getQuery()
			.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, params.presentationUrl);
	}

	return search;
});

QUERIES.set("Material.BaseProduct", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Material");
	search.setLimitPath(contextUrl, pathCreate("Attributes", "BOMMainMaterials"));
	return search;
});

QUERIES.set("ColorMaterial.BaseProduct", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ColorMaterial");
	search.setLimitPath(contextUrl, pathCreate("ProductColors"));
	return search;
});

QUERIES.set("Colorway.BaseProduct", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Colorway");
	search.setLimitPath(contextUrl, pathCreate("ProductColors"));
	return search;
});

QUERIES.set("Colorway.ColorSpecification", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("Colorway");

	if (params?.presentationUrl) {
		search.getQuery()
			.Node(xml.PROP_URL, xml.OP_EQUALS, params.presentationUrl, "Child:__Parent__/Parent:PPProducts");
	}

	search.getQuery()
		.RefAttr("ColorSpecification", xml.OP_EQUALS, contextUrl);
	return search;
});

QUERIES.set("ColorMaterial.ColorSpecification", (contextUrl?: cnl, params?: ParameterMap) => {
	const search = new Search()
		.byType("ColorMaterial");

	if (params?.presentationUrl) {
		search.getQuery()
			.Node(xml.PROP_URL, xml.OP_EQUALS, params.presentationUrl, "Child:__Parent__/Parent:PPProducts");
	}

	search.getQuery()
		.RefAttr("ColorSpecification", xml.OP_EQUALS, contextUrl);
	return search;
});

QUERIES.set("ColorMaterial.Material", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ColorMaterial");
	search.setLimitPath(contextUrl, pathCreate("ProductColors"));
	return search;
});

QUERIES.set("Colorway.Style", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Colorway");
	search.setLimitPath(contextUrl, pathCreate("ProductColors"));
	return search;
});

QUERIES.set("ProductPresentationSet.ProductPresentation", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ProductPresentationSet");
	search.setLimitPath(contextUrl, pathCreate("Sets"));
	return search;
});

QUERIES.set("ProductColor.ProductPresentation", (contextUrl?: cnl) => {
	const search = new Search();
	search.setLimitPath(contextUrl, pathCreate("PPProducts", "ProductColors"));
	return search;
});

QUERIES.set("Material.ProductPresentation.MainMaterials", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Material");
	search.setLimitPath(contextUrl, pathCreate("PPProducts", "Attributes", "BOMMainMaterials"));
	return search;
});

QUERIES.set("Theme.ProductPresentation", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Theme");
	search.setLimitPath(contextUrl, pathCreate("PPProducts", "Theme"));
	return search;
});

QUERIES.set("ThemeMaster.ProductPresentation", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ThemeMaster");
	search.setLimitPath(contextUrl, pathCreate("PPProducts", "Theme", "__Parent__"));
	return search;
});

QUERIES.set("ColorSpecification.ProductPresentationSet", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ColorSpecification");
	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProductColor", "ColorSpecification"));
	return search;
});

QUERIES.set("Theme.ProductPresentationSet", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Theme");
	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProduct", "Theme"));
	return search;
});

QUERIES.set("ThemeMaster.ProductPresentationSet", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ThemeMaster");
	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProduct", "Theme", "__Parent__"));
	return search;
});

QUERIES.set("Colorway.ProductPresentationSet", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Colorway");
	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProductColor"));
	return search;
});

QUERIES.set("Material.ProductPresentationSet.MainMaterials", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("Material");
	search.setLimitPath(contextUrl, pathCreate("Items", "PPIProduct", "Attributes", "BOMMainMaterials"));
	return search;
});

QUERIES.set("ProductPresentationSetItem.ProductPresentationSet", (contextUrl?: cnl) => {
	const search = new Search()
		.byType("ProductPresentationSetItem");
	search.setLimitPath(contextUrl, pathCreate("Items"));
	return search;
});

QUERIES.set("ProofTemplate.Active", () => {
	const search = new Search()
		.byType("ProofTemplate");
	search.getQuery()
		.Attr("Active", xml.OP_EQUALS, true);
	return search;
});

QUERIES.set("DataSheetPage.CanvasContainer", (contextUrl: cnl) => {
	const search = new Search()
		.byType("DataSheetPage");
	search.setLimitPath(contextUrl, pathCreate("Pages"));
	return search;
});

QUERIES.set("User.Available", () => {
	const search = new Search({ Module: "UserProvider", Operation: "GetAvailableUsers" });
	search.getQuery()
		.Attr("Active", xml.OP_EQUALS, true)
		.Attr("UserType", xml.OP_EQUALS, "UserType:User");
	return search;
});

// ---- API ----

export type SearchCallback = (nodes: C8Node[], response: JsonResponse) => void;

/** return QueryBuilder function for given data store id */
export function getQueryBuilderById(id: string): QueryBuilder {
	return QUERIES.get(id);
}

/** return search object for given data store id */
export function getSearch(id: string, contextUrl?: cnl, params?: ParameterMap): Search | undefined {
	const qb = getQueryBuilderById(id);
	return qb?.(contextUrl, params);
}

/** make server request for given data store and cache results */
export async function runSearch(search: Search, cache?: ICache, callback?: SearchCallback): Promise<JsonResponse> {
	const response = await search.execute();

	runInAction(() => {
		cache?.cacheResponse(response);
		callback?.(response.getResultNodes(), response);
	});

	return response;
}
